<template>
  <div
    ref="drawer"
    :tabindex="tabIndex"
    :style="!modal && isVisible ? drawerWidth : null"
    :class="[
      'pdl-drawer',
      `pdl-drawer--${placement}`,
      {
        invisible: !isVisible,
        absolute: bound,
        'pdl-drawer--open': isVisible,
        'pdl-drawer--hide-overlay': !modal,
      },
    ]"
    :aria-modal="modal"
  >
    <div v-if="modal" class="pdl-drawer__mask" @click="closeDrawer" />
    <div
      class="pdl-drawer__content-wrapper"
      :style="modal ? drawerWidth : null"
      :class="{'pdl-drawer--auto-height': autoHeight}"
    >
      <section class="pdl-drawer__content">
        <!-- @slot Slot for content. -->
        <slot name="content" />
        <pdl-button
          v-if="showClose"
          text
          icon-only
          icon="close"
          label="Close"
          class="pdl-drawer__close-btn"
          @click="closeDrawer"
        />
      </section>
    </div>
  </div>
</template>

<script>
import {PdlButton} from '@pedal/pdl-button';

export default {
  name: 'PdlDrawer',
  components: {PdlButton},
  props: {
    /**
     * Side of screen from which drawer appears.
     */
    placement: {
      type: String,
      default: 'right',
    },
    /**
     * Set width of vertical drawers.
     */
    size: {
      type: String,
      default: '16rem',
    },
    /**
     * Make drawer bound to a specific container.
     */
    bound: {
      type: Boolean,
      default: false,
    },
    /**
     * Display close button.
     */
    showClose: {
      type: Boolean,
      default: false,
    },
    /**
     * Opens the drawer.
     */
    isOpen: {
      type: Boolean,
      default: false,
    },
    /**
     * Display of semi-transparent overlay over page.
     */
    modal: {
      type: Boolean,
      default: true,
    },
    /**
     * Set height of vertical drawers to the content size.
     */
    autoHeight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isVisible: this.isOpen,
      previousActiveElement: null,
      focusableElements: [],
      mutationObserver: null,
    };
  },
  computed: {
    verticalDrawer() {
      return this.placement === 'left' || this.placement === 'right';
    },
    drawerWidth() {
      return this.verticalDrawer ? `width: ${this.size}` : '';
    },
    tabIndex() {
      return this.isOpen ? 0 : -1;
    },
  },
  watch: {
    isOpen(value) {
      if (value) {
        this.openDrawer();
      } else {
        this.closeDrawer();
      }
    },
  },
  beforeUnmount() {
    this.teardownMutationObserver();
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    openDrawer() {
      this.previousActiveElement = document.activeElement;
      this.isVisible = true;
      this.$emit('open');
      this.$nextTick(() => {
        this.focusableElements = this.getAllFocusableElements();
        this.setupMutationObserver();
        this.focusableElements?.[0]?.focus({preventScroll: true});
        document.addEventListener('keydown', this.handleKeydown);
      });
    },
    closeDrawer() {
      this.$emit('close');
      this.isVisible = false;
      document.removeEventListener('keydown', this.handleKeydown);
      this.teardownMutationObserver();
      this.$nextTick(() => {
        this.previousActiveElement?.focus();
      });
    },
    getAllFocusableElements() {
      const drawer = this.$refs.drawer;
      if (!drawer || !(drawer instanceof HTMLElement)) {
        console.warn('You need to pass a parent HTMLElement');
        return [];
      }

      const elements = Array.from(
        drawer.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)'
        )
      );

      return [drawer, ...elements];
    },
    handleKeydown(event) {
      if (event.key === 'Escape' || (!this.focusableElements?.length && event.key === 'Tab')) {
        event.preventDefault();
        this.closeDrawer();
      } else {
        this.focusTrap(event, this.focusableElements);
      }
    },
    focusTrap(event) {
      const firstFocusableElement = this.focusableElements[0];
      const lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];

      if (event.key !== 'Tab') return;

      if (event.shiftKey && document.activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    },
    setupMutationObserver() {
      if (this.mutationObserver) this.mutationObserver.disconnect();
      const drawer = this.$refs.drawer;
      if (!drawer) return;
      this.mutationObserver = new MutationObserver(() => {
        this.focusableElements = Array.from(this.getAllFocusableElements());
      });
      this.mutationObserver.observe(drawer, {childList: true, subtree: true});
    },
    teardownMutationObserver() {
      if (this.mutationObserver) this.mutationObserver.disconnect();
      this.mutationObserver = null;
    },
  },
};
</script>

<style lang="scss">
@import 'Drawer.scss';
</style>
